// Sabit Rapor — Ürün Listesi (Raporlar → Sabit Raporlar → Tanımlamalar).
//
// İki ekran (Sabit Rapor deseni):
//   1) ProductListCriteriaPage — kriter formu (hepsi opsiyonel): Distribütör, Üretici, Marka,
//      Alt Kategori, Ürün, Durum, Ürün Cinsi (Stoklu/Hizmet) ve serbest metin araması.
//      Kriterler değiştikçe "N ürün kritere uyuyor" canlı önizlemesi gösterilir.
//   2) ProductListListPage — /api/detail-reports/product-list'ten Product master kartlarının
//      düz listesi. Stok/hareket İÇERMEZ; tanım kartıdır. Grid, Excel/PDF ve pivot ReportGrid'den.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authHeaders, exportToExcel } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { ReportGrid } from './reports/ReportGrid';
import { MultiSelect } from './reports/MultiSelect';
import { STOCK_TYPE_OPTIONS } from './stockTypes';

const ENDPOINT = '/api/detail-reports/product-list';
const REPORT_CODE = '5010';
const REPORT_TITLE = 'Ürün Listesi';

// ── Yardımcılar ──
function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}
const pick = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k]; return undefined; };
const optId = (o) => pick(o, 'id', 'Id');
const optCode = (o) => pick(o, 'textId', 'TextId', 'erpId', 'ErpId');
const optName = (o) => pick(o, 'name', 'Name', 'description', 'Description', 'title', 'Title');
const optLabel = (o) => { const code = optCode(o); const name = optName(o); if (code && name) return `${code} - ${name}`; return name || code || '—'; };
const nf2 = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });
const nfM = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nfC = new Intl.NumberFormat('tr-TR');
const fmtNum = (v) => (v === null || v === undefined || v === '' ? '—' : nf2.format(Number(v) || 0));
const fmtMoney = (v) => (v === null || v === undefined ? '—' : `${nfM.format(Number(v) || 0)} ₺`);
const fmtPct = (v) => (v === null || v === undefined ? '—' : `%${nf2.format(Number(v) || 0)}`);
const fmtCount = (v) => nfC.format(Number(v) || 0);
const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

function useLookups() {
  const [lk, setLk] = useState({ vendors: [], producers: [], groups: [], subGroups: [], dynamicGroups: [], products: [] });
  useEffect(() => {
    const headers = authHeaders();
    const load = (url) => fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then(normalizeList).catch(() => []);
    // Bağımsız yükle — biri ağır/askıda kalırsa diğerleri beklemesin.
    load('/api/vendors/Select').then((vendors) => setLk((s) => ({ ...s, vendors })));
    load('/api/producers/Select').then((producers) => setLk((s) => ({ ...s, producers })));
    load('/api/productgroups/Select').then((groups) => setLk((s) => ({ ...s, groups })));
    load('/api/productsubgroups/Select').then((subGroups) => setLk((s) => ({ ...s, subGroups })));
    load('/api/productdynamicgroups/Select').then((dynamicGroups) => setLk((s) => ({ ...s, dynamicGroups })));
    load('/api/products/Select').then((products) => setLk((s) => ({ ...s, products })));
  }, []);
  return lk;
}

/* ════════════════════════════ STYLES ════════════════════════════ */
const c = {
  page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 4 },
  card: { background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 18, boxShadow: 'var(--shadow-card)', padding: 20 },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  cardHeadLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cardHeadIcon: { width: 40, height: 40, borderRadius: 12, background: 'var(--bg-input, #eef2ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent, #0073ea)' },
  cardTitle: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--text-secondary, #475569)' },
  input: { padding: '11px 12px', borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13.5, width: '100%', boxSizing: 'border-box' },
  sectionSep: { display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0 4px', fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', color: 'var(--text-secondary, #94a3b8)' },
  sepLine: { flex: 1, height: 1, background: 'var(--border-color, #e5e7eb)' },
  btn: { padding: '11px 20px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost: { background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)' },
  btnPrimary: { background: 'var(--accent-strong, #1e3a8a)', color: '#fff' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '44px 24px', textAlign: 'center' },
  emptyIcon: { width: 64, height: 64, borderRadius: 18, background: 'var(--bg-input, #eef2f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #94a3b8)', marginBottom: 6 },
  countChip: { marginTop: 4, padding: '8px 16px', borderRadius: 999, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 13.5 },
};
function Field({ label, children }) { return <label style={c.field}><span style={c.label}>{label}</span>{children}</label>; }
function Select({ value, onChange, options, placeholder = 'Tümü' }) {
  return (
    <select style={c.input} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={String(o.value ?? optId(o))} value={String(o.value ?? optId(o))}>{o.label !== undefined ? o.label : optLabel(o)}</option>
      ))}
    </select>
  );
}

const STATUS_OPTIONS = [{ value: '1', label: 'Aktif' }, { value: '0', label: 'Pasif' }, { value: '2', label: 'Beklemede' }];
const SERVICE_OPTIONS = [{ value: 'false', label: 'Stoklu Ürün' }, { value: 'true', label: 'Hizmet' }];
// Stok tipi — tek doğruluk kaynağı stockTypes.js (backend StockType enum'unun aynası).
const STOCK_TYPE_CHOICES = STOCK_TYPE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }));
// Lookup listesini MultiSelect'in beklediği {value,label} şekline çevirir.
const asChoices = (arr) => arr.map((o) => ({ value: String(optId(o)), label: optLabel(o) }));

/* ═══════════════════════ 1) KRİTER EKRANI ═══════════════════════ */
export function ProductListCriteriaPage() {
  usePageMeta(REPORT_TITLE, 'Sabit Raporlar › Tanımlamalar');
  const lk = useLookups();
  // Kriterler ÇOKLU SEÇİM: her biri string[] tutar, boş dizi = "Tümü".
  const [vendorIds, setVendorIds] = useState([]);
  const [producerIds, setProducerIds] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [subGroupIds, setSubGroupIds] = useState([]);
  const [dynamicGroupIds, setDynamicGroupIds] = useState([]);
  const [productIds, setProductIds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [stockTypes, setStockTypes] = useState([]);
  const [isService, setIsService] = useState('');
  // Ürün kodu ve adı AYRI kriter (tek "Ürün" alanı ikisini birlikte gösteriyordu).
  const [q, setQ] = useState('');
  const [preview, setPreview] = useState(null);

  // Distribütör seçilince ürün listesi süzülür (ürünün VendorId'si boşsa tenant genelidir).
  // Distribütör seçilince ürün listesi KATI süzülür: yalnız o distribütöre bağlı ürünler kalır.
  // (Önce distribütörü boş olan ürünler de listede bırakılıyordu; bu veritabanında ürünlerin
  // %90'ının distribütörü boş olduğu için seçim yapmak listeyi hiç değiştirmiyordu.)
  // Seçim yoksa filtre uygulanmaz → tüm ürünler.
  const productOptions = useMemo(() => {
    const list = vendorIds.length
      ? lk.products.filter((p) => vendorIds.includes(String(pick(p, 'vendorId', 'VendorId') ?? '')))
      : lk.products;
    return list.map((p) => ({ value: String(optId(p)), label: optLabel(p) }));
  }, [lk.products, vendorIds]);

  const query = useCallback(() => {
    const p = new URLSearchParams();
    const csv = (key, arr) => { if (arr.length) p.set(key, arr.join(',')); };
    csv('vendorIds', vendorIds);
    csv('producerIds', producerIds);
    csv('groupIds', groupIds);
    csv('subGroupIds', subGroupIds);
    csv('dynamicGroupIds', dynamicGroupIds);
    csv('productIds', productIds);
    csv('statuses', statuses);
    csv('stockTypes', stockTypes);
    if (isService) p.set('isService', isService);
    if (q.trim()) p.set('q', q.trim());
    return p;
  }, [vendorIds, producerIds, groupIds, subGroupIds, dynamicGroupIds, productIds, statuses, stockTypes, isService, q]);

  // Canlı önizleme — kriter değiştikçe kaç ürün geleceğini söyler.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      const p = query();
      p.set('previewOnly', 'true');
      fetch(`${ENDPOINT}?${p.toString()}`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => { if (!cancelled) setPreview(json?.data?.totals ?? json?.totals ?? null); })
        .catch(() => { if (!cancelled) setPreview(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const clear = () => { setVendorIds([]); setProducerIds([]); setGroupIds([]); setSubGroupIds([]); setDynamicGroupIds([]); setProductIds([]); setStatuses([]); setStockTypes([]); setIsService(''); setQ(''); };

  const showReport = () => {
    const p = query();
    p.set('embed', 'true');
    const w = Math.min(1720, window.screen.availWidth - 40);
    const h = Math.min(940, window.screen.availHeight - 60);
    const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2));
    window.open(`/reports/product-list-list?${p.toString()}`, 'urunListesi5010',
      `popup=yes,noopener,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
  };

  return (
    <div style={c.page}>
      <div style={c.card}>
        <div style={c.cardHead}>
          <div style={c.cardHeadLeft}>
            <span style={c.cardHeadIcon}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            </span>
            <span style={c.cardTitle}>Kriterler</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" style={{ ...c.btn, ...c.btnGhost }} onClick={clear}>✕ Temizle</button>
            <button type="button" style={{ ...c.btn, ...c.btnPrimary }} onClick={showReport}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              Raporu Göster
            </button>
          </div>
        </div>

        <div style={c.grid}>
          <Field label="Distribütör">
            <MultiSelect options={asChoices(lk.vendors)} value={vendorIds} onChange={(v) => { setVendorIds(v); setProductIds([]); }} />
          </Field>
          <Field label="Üretici">
            <MultiSelect options={asChoices(lk.producers)} value={producerIds} onChange={setProducerIds} />
          </Field>
          <Field label="Ürün Grubu">
            <MultiSelect options={asChoices(lk.groups)} value={groupIds} onChange={setGroupIds} />
          </Field>
          <Field label="Ek Grubu">
            <MultiSelect options={asChoices(lk.subGroups)} value={subGroupIds} onChange={setSubGroupIds} />
          </Field>
          <Field label="Dinamik Grup">
            <MultiSelect options={asChoices(lk.dynamicGroups)} value={dynamicGroupIds} onChange={setDynamicGroupIds} />
          </Field>
        </div>

        <div style={c.sectionSep}><span>ÜRÜN FİLTRELERİ</span><span style={c.sepLine} /></div>
        <div style={c.grid}>
          <Field label="Ürün Seç">
            <MultiSelect
              options={productOptions}
              value={productIds}
              onChange={setProductIds}
              placeholder={vendorIds.length ? 'Tümü' : 'Tümü (distribütör seçince süzülür)'}
            />
          </Field>
          <Field label="Stok Durumu">
            <MultiSelect options={STOCK_TYPE_CHOICES} value={stockTypes} onChange={setStockTypes} />
          </Field>
          <Field label="Durum">
            <MultiSelect options={STATUS_OPTIONS} value={statuses} onChange={setStatuses} />
          </Field>
          <Field label="Ürün Cinsi"><Select value={isService} onChange={setIsService} options={SERVICE_OPTIONS} /></Field>
          <Field label="Serbest Arama">
            <input style={c.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kod, ad, kısa ad ve takip kodunun hepsinde arar" />
          </Field>
        </div>
      </div>

      <div style={c.card}>
        <div style={c.emptyState}>
          <span style={c.emptyIcon}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="M3.3 7 12 12l8.7-5" /><path d="M12 22V12" /></svg>
          </span>
          <strong style={{ fontSize: 17, color: 'var(--text-primary, #0f172a)' }}>Rapor ayrı ekranda görüntülenir</strong>
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary, #64748b)', maxWidth: 620 }}>
            Kriterlerin tamamı opsiyoneldir; boş bırakılırsa yetkin dahilindeki tüm ürünler listelenir.
            <strong> Raporu Göster</strong>'e basın — rapor tam ekran açılır, oradan Excel/PDF alabilir, Pivot'a geçebilirsiniz.
          </span>
          {preview && <div style={c.countChip}>{fmtCount(preview.urunSayisi)} ürün kritere uyuyor</div>}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ 2) RAPOR EKRANI ═══════════════════════ */
const codeChipStyle = { padding: '3px 10px', borderRadius: 8, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 12.5 };
function DurumChip({ v }) {
  if (!v || v === '—') return '—';
  const tone = v === 'Aktif' ? { bg: 'rgba(22,163,74,0.10)', fg: '#16a34a' }
    : v === 'Pasif' ? { bg: 'var(--bg-input, #f1f5f9)', fg: '#94a3b8' }
      : { bg: 'rgba(217,119,6,0.10)', fg: '#d97706' };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: tone.bg, color: tone.fg, fontWeight: 700, fontSize: 12 }}>{v}</span>;
}

// İlk kolon sticky. Sayısal kolonlarda num/fmt; fiyat ve KDV'de agg:'none' — satırlar arası
// toplamları anlamsız (birim fiyat toplanmaz).
const COLUMNS = [
  { key: 'urunKodu', label: 'Ürün Kodu', sticky: true, cell: (row) => (row.urunKodu ? <span style={codeChipStyle}>{row.urunKodu}</span> : '—') },
  { key: 'urunAdi', label: 'Ürün Adı', bold: true },
  { key: 'kisaAd', label: 'Kısa Ad', fmt: dash },
  { key: 'takipKodu', label: 'Takip Kodu', fmt: dash },
  { key: 'uretici', label: 'Üretici', fmt: dash },
  { key: 'urunGrubu', label: 'Ürün Grubu', fmt: dash },
  { key: 'ekGrubu', label: 'Ek Grubu', fmt: dash },
  { key: 'dinamikGrup', label: 'Dinamik Grup', fmt: dash },
  { key: 'stokTipi', label: 'Stok Durumu', fmt: dash },
  { key: 'distributor', label: 'Distribütör', fmt: dash },
  { key: 'anaBirim', label: 'Ana Birim', fmt: dash },
  { key: 'koliIci', label: 'Koli İçi', num: true, fmt: fmtNum },
  { key: 'listeFiyat', label: 'Liste Fiyatı', num: true, agg: 'none', fmt: fmtMoney },
  { key: 'kdv', label: 'KDV', num: true, agg: 'none', fmt: fmtPct },
  { key: 'minSiparis', label: 'Min. Sipariş', num: true, agg: 'none', fmt: fmtNum },
  { key: 'urunTipi', label: 'Ürün Tipi', fmt: dash },
  { key: 'hizmet', label: 'Ürün Cinsi' },
  { key: 'agirlik', label: 'Ağırlık', num: true, agg: 'none', fmt: fmtNum },
  { key: 'hacim', label: 'Hacim', num: true, agg: 'none', fmt: fmtNum },
  { key: 'litre', label: 'Litre', num: true, agg: 'none', fmt: fmtNum },
  { key: 'durum', label: 'Durum', cell: (row) => <DurumChip v={row.durum} /> },
  { key: 'aciklama', label: 'Açıklama', fmt: dash },
];

const l = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-page, #f1f5f9)' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 20px', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #e5e7eb)', position: 'sticky', top: 0, zIndex: 5 },
  topLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  titleIcon: { width: 42, height: 42, borderRadius: 12, background: 'var(--accent-strong, #1e3a8a)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  subtitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary, #64748b)' },
  iconBtn: { width: 42, height: 42, borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  critBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', flexWrap: 'wrap', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #eef2f7)', fontSize: 12.5 },
  critChip: { padding: '6px 12px', borderRadius: 999, background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)', fontWeight: 700 },
  empty: { padding: 60, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
};
const hesc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
function printAsPdf({ rows, subtitle }) {
  const head = COLUMNS.map((col) => `<th style="text-align:${col.num ? 'right' : 'left'}">${hesc(col.label)}</th>`).join('');
  const body = rows.map((row) => `<tr>${COLUMNS.map((col) => {
    let v = row[col.key];
    if (col.fmt) v = col.fmt(v);
    return `<td style="text-align:${col.num ? 'right' : 'left'}">${hesc(v ?? '')}</td>`;
  }).join('')}</tr>`).join('');
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${REPORT_TITLE}</title><style>*{box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:14px}h1{font-size:14px;color:#1e3a8a;margin:0 0 4px}.crit{font-size:9.5px;color:#475569;margin-bottom:8px}table{width:100%;border-collapse:collapse}th{background:#eef2f7;color:#475569;font-size:8px;text-transform:uppercase;padding:4px 5px;border-bottom:1.5px solid #cbd5e1}td{font-size:9px;padding:3px 5px;border-bottom:1px solid #eef2f7;white-space:nowrap}@page{size:A3 landscape;margin:10mm}</style></head><body><h1>${REPORT_TITLE}</h1><div class="crit">${hesc(subtitle)}</div><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document; doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1000); };
}

export function ProductListListPage() {
  usePageMeta(REPORT_TITLE, 'Sabit Rapor');
  const [sp] = useSearchParams();
  const lk = useLookups();

  const params = useMemo(() => {
    const o = {};
    for (const k of ['vendorIds', 'producerIds', 'groupIds', 'subGroupIds', 'dynamicGroupIds', 'productIds', 'statuses', 'stockTypes', 'isService', 'q']) {
      const v = sp.get(k); if (v) o[k] = v;
    }
    return o;
  }, [sp]);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Excel/PDF kullanıcının GÖRDÜĞÜ kümeyi almalı → grid görünen satırları buraya bildirir.
  const [visibleRows, setVisibleRows] = useState(null);
  const [nowLabel] = useState(() => new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${ENDPOINT}?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rapor alınamadı (${res.status})`);
      const json = await res.json();
      const data = json?.data ?? json;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotals(data?.totals ?? null);
    } catch (e) {
      setError(e.message || 'Hata'); setRows([]);
    } finally { setLoading(false); }
  }, [params]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportRows = visibleRows ?? rows;
  // Çoklu seçim: "a,b" → "Ad1 + Ad2"; boşsa "Tümü".
  const namesOf = (arr, csv) => {
    if (!csv) return 'Tümü';
    return csv.split(',')
      .map((id) => { const o = arr.find((x) => String(optId(x)) === id); return o ? optName(o) : id; })
      .join(' + ');
  };
  const labelsOf = (opts, csv) => (csv
    ? csv.split(',').map((v) => opts.find((o) => String(o.value) === v)?.label ?? v).join(' + ')
    : 'Tümü');
  const vendorName = namesOf(lk.vendors, params.vendorIds);
  const producerName = namesOf(lk.producers, params.producerIds);
  const groupName = namesOf(lk.groups, params.groupIds);
  const dynGroupName = namesOf(lk.dynamicGroups, params.dynamicGroupIds);
  const statusLabel = labelsOf(STATUS_OPTIONS, params.statuses);
  const stockTypeLabel = labelsOf(STOCK_TYPE_CHOICES, params.stockTypes);
  const searchBits = [
    params.q && `Arama: ${params.q}`,
  ].filter(Boolean).join(' · ');
  const summaryText = `Distribütör: ${vendorName} · Üretici: ${producerName} · Ürün Grubu: ${groupName} · Dinamik Grup: ${dynGroupName} · Stok Durumu: ${stockTypeLabel} · Durum: ${statusLabel}${searchBits ? ` · ${searchBits}` : ''}`;

  const onExcel = () => {
    const cols = COLUMNS.map((col) => ({ field: col.key, headerName: col.label, valueGetter: ({ row }) => row[col.key] }));
    exportToExcel(exportRows, cols, `${REPORT_CODE}_urun_listesi`);
  };

  return (
    <div style={l.wrap}>
      <div style={l.topbar}>
        <div style={l.topLeft}>
          <span style={l.titleIcon}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="M3.3 7 12 12l8.7-5" /><path d="M12 22V12" /></svg>
          </span>
          <div>
            <div style={l.title}>{REPORT_TITLE}</div>
            <div style={l.subtitle}>
              {loading ? 'Yükleniyor…' : `${fmtCount(totals?.urunSayisi ?? rows.length)} ürün · ${fmtCount(totals?.aktifSayisi ?? 0)} aktif · ${nowLabel}`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" style={l.iconBtn} title="PDF İndir" onClick={() => printAsPdf({ rows: exportRows, subtitle: summaryText })}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Excel'e Aktar" onClick={onExcel}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#16794c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="m9 15 3 3 3-3" /><path d="M12 12v6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Yazdır" onClick={() => window.print()}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          </button>
        </div>
      </div>

      <div style={l.critBar}>
        <span style={{ fontWeight: 800, color: 'var(--text-secondary, #64748b)' }}>KRİTERLER</span>
        <span style={l.critChip}>Distribütör: {vendorName}</span>
        <span style={l.critChip}>Üretici: {producerName}</span>
        <span style={l.critChip}>Ürün Grubu: {groupName}</span>
        <span style={l.critChip}>Dinamik Grup: {dynGroupName}</span>
        <span style={l.critChip}>Stok Durumu: {stockTypeLabel}</span>
        <span style={l.critChip}>Durum: {statusLabel}</span>
        {searchBits && <span style={l.critChip}>{searchBits}</span>}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 20px 20px' }}>
        {error && <div style={{ ...l.empty, color: '#dc2626' }}>{error}</div>}
        {!error && !loading && (
          <ReportGrid
            reportKey="product-list"
            columns={COLUMNS}
            rows={rows}
            onVisibleRowsChange={setVisibleRows}
          />
        )}
        {loading && <div style={l.empty}>Yükleniyor…</div>}
      </div>
    </div>
  );
}
